import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import {
  ChevronLeft,
  Plus,
  Building2,
  Users,
  MessageSquare,
  Pencil,
  Trash2,
  Check,
  X,
  Settings,
} from "lucide-react";
import { toast } from "sonner";

const HotelManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingHotel, setEditingHotel] = useState(null);

  const [newHotel, setNewHotel] = useState({
    name: "",
    conti_aperti: false,
    channels: ["Front Office", "Lost and Found", "Cucina", "Gruppi ed Eventi", "Manutenzione"],
  });
  const [channelInput, setChannelInput] = useState("");

  const fetchHotels = async () => {
    try {
      const res = await axios.get(`${API}/hotels/all`);
      setHotels(res.data);
    } catch {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role !== "super_admin") {
      navigate("/");
      return;
    }
    fetchHotels();
  }, []);

  const handleCreate = async () => {
    if (!newHotel.name.trim()) {
      toast.error("Nome hotel obbligatorio");
      return;
    }
    try {
      await axios.post(`${API}/hotels`, {
        name: newHotel.name.trim(),
        features: { conti_aperti: newHotel.conti_aperti },
        channels: newHotel.channels,
      });
      toast.success("Hotel creato!");
      setCreateOpen(false);
      setNewHotel({ name: "", conti_aperti: false, channels: ["Front Office", "Lost and Found", "Cucina", "Gruppi ed Eventi", "Manutenzione"] });
      fetchHotels();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Errore nella creazione");
    }
  };

  const handleToggleContiAperti = async (hotel) => {
    try {
      const newVal = !hotel.features?.conti_aperti;
      await axios.patch(`${API}/hotels/${hotel.slug}`, {
        features: { conti_aperti: newVal },
      });
      toast.success(newVal ? "Conti Aperti attivato" : "Conti Aperti disattivato");
      fetchHotels();
    } catch {
      toast.error("Errore");
    }
  };

  const handleToggleChecklist = async (hotel) => {
    try {
      const newVal = !hotel.features?.checklist;
      await axios.patch(`${API}/hotels/${hotel.slug}`, {
        features: { checklist: newVal },
      });
      toast.success(newVal ? "Check List attivata" : "Check List disattivata");
      fetchHotels();
    } catch {
      toast.error("Errore");
    }
  };

  const handleDelete = async (hotel) => {
    if (!window.confirm(`Eliminare l'hotel "${hotel.name}"? Questa azione è irreversibile.`)) return;
    try {
      await axios.delete(`${API}/hotels/${hotel.slug}`);
      toast.success("Hotel eliminato");
      fetchHotels();
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleRename = async (hotel) => {
    const newName = window.prompt("Nuovo nome:", hotel.name);
    if (!newName || !newName.trim()) return;
    try {
      await axios.patch(`${API}/hotels/${hotel.slug}`, { name: newName.trim() });
      toast.success("Nome aggiornato");
      fetchHotels();
    } catch {
      toast.error("Errore");
    }
  };

  const addChannel = () => {
    if (!channelInput.trim()) return;
    if (!newHotel.channels.includes(channelInput.trim())) {
      setNewHotel({ ...newHotel, channels: [...newHotel.channels, channelInput.trim()] });
    }
    setChannelInput("");
  };

  const removeChannel = (ch) => {
    setNewHotel({ ...newHotel, channels: newHotel.channels.filter((c) => c !== ch) });
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="sticky top-0 z-50 bg-stone-50/80 backdrop-blur-xl border-b border-stone-200">
        <div className="px-4 py-4 flex items-center gap-3">
          <button onClick={() => navigate("/")} className="p-1">
            <ChevronLeft className="w-5 h-5 text-stone-600" />
          </button>
          <div className="flex-1">
            <h1 className="font-outfit text-lg font-bold text-stone-900">Gestione Hotel</h1>
            <p className="text-xs text-stone-500">Super Admin</p>
          </div>
          <Button onClick={() => setCreateOpen(true)} className="rounded-full bg-stone-900 hover:bg-stone-800" data-testid="create-hotel-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nuovo Hotel
          </Button>
        </div>
      </header>

      <main className="p-4 space-y-4">
        {loading ? (
          <div className="space-y-3">
            {[1, 2].map((i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-stone-200 animate-pulse">
                <div className="h-6 bg-stone-200 rounded w-1/3 mb-3" />
                <div className="h-4 bg-stone-100 rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : hotels.length === 0 ? (
          <div className="text-center py-16 text-stone-400">
            <Building2 className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <p className="text-lg">Nessun hotel configurato</p>
            <p className="text-sm mt-1">Crea il tuo primo hotel</p>
          </div>
        ) : (
          hotels.map((hotel) => (
            <div
              key={hotel.slug}
              className="bg-white rounded-2xl border border-stone-200 overflow-hidden"
              data-testid={`hotel-card-${hotel.slug}`}
            >
              <div className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h2 className="font-outfit text-xl font-bold text-stone-900">{hotel.name}</h2>
                    <p className="text-xs text-stone-400 font-mono">/{hotel.slug}</p>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => handleRename(hotel)} className="p-2 rounded-lg text-stone-400 hover:text-blue-600 hover:bg-blue-50">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(hotel)} className="p-2 rounded-lg text-stone-400 hover:text-red-600 hover:bg-red-50">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="flex gap-4 mb-4">
                  <div className="flex items-center gap-1.5 text-sm text-stone-600">
                    <Users className="w-4 h-4" />
                    <span>{hotel.user_count} utenti</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-sm text-stone-600">
                    <MessageSquare className="w-4 h-4" />
                    <span>{hotel.channel_count} canali</span>
                  </div>
                </div>

                {/* Features */}
                <div className="flex items-center gap-3 flex-wrap">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={hotel.features?.conti_aperti || false}
                      onChange={() => handleToggleContiAperti(hotel)}
                      className="rounded border-stone-300"
                    />
                    <span className="text-sm text-stone-700">Conti Aperti</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={hotel.features?.checklist || false}
                      onChange={() => handleToggleChecklist(hotel)}
                      className="rounded border-stone-300"
                      data-testid={`toggle-checklist-${hotel.slug}`}
                    />
                    <span className="text-sm text-stone-700">Check List</span>
                  </label>
                </div>
              </div>
            </div>
          ))
        )}
      </main>

      {/* Create Hotel Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit">Nuovo Hotel</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Nome Hotel</Label>
              <Input
                value={newHotel.name}
                onChange={(e) => setNewHotel({ ...newHotel, name: e.target.value })}
                placeholder="Es: Grand Hotel Roma"
                className="mt-1"
                autoFocus
              />
            </div>

            <div>
              <Label>Canali da creare</Label>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {newHotel.channels.map((ch) => (
                  <span key={ch} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-stone-100 text-sm text-stone-700">
                    {ch}
                    <button onClick={() => removeChannel(ch)} className="text-stone-400 hover:text-red-500">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2 mt-2">
                <Input
                  value={channelInput}
                  onChange={(e) => setChannelInput(e.target.value)}
                  placeholder="Aggiungi canale..."
                  className="flex-1"
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addChannel(); } }}
                />
                <Button variant="outline" size="sm" onClick={addChannel}>+</Button>
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={newHotel.conti_aperti}
                onChange={(e) => setNewHotel({ ...newHotel, conti_aperti: e.target.checked })}
                className="rounded border-stone-300"
              />
              <span className="text-sm text-stone-700">Attiva Conti Aperti</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Annulla</Button>
            <Button onClick={handleCreate} className="bg-stone-900 hover:bg-stone-800">
              <Plus className="w-4 h-4 mr-2" />
              Crea Hotel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HotelManagement;
